import React, { SetStateAction, useEffect, useRef, useState } from 'react';
import { useTable, usePagination, useRowSelect, useSortBy, useResizeColumns, useColumnOrder } from 'react-table';
import { useSticky } from 'react-table-sticky';
import { WarehouseType } from '../../../constants/Warehouses/WarehouseType';
import ComponentsLoader from 'components/ComponentsLoader/ComponentsLoader';
import './Tablestyles.scss';
import type { IRowData, ITableSettingsLegacy } from 'constants/Table/interfaces';
import CustomTableCell from 'components/Tables/WarehouseTable/CustomTableCell';
import HeaderRow from 'components/Tables/StocksFBSTable/HeaderRow';
import TableFooter from '../DefaultTable/TableFooter';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import type { AnyAction } from '@reduxjs/toolkit';
import {
    setStockToProductAtWarehouse,
    unsetStockOfProductAtWarehouse,
} from '../../../redux/stocks/reducer';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import IndeterminateCheckbox from '../WarehouseTable/IndeterminateCheckbox';
import url from '../../../utils/UrlHelper';
import sellerProductsService from '../../../api/services/sellerProductsService';
import stocksService from '../../../api/services/stocksService';
import { setTemporaryNotifications } from '../../../redux/notifications/actions';
import { defaultNotificationCreator } from '../../../templates/Notifications';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { ReactComponent as ImagePlaceholder } from '../../../assets/icons/new/UI/Image.svg';
import { ReactComponent as CopyValueIcon } from '../../../assets/icons/new/UI/CopyValue.svg';

type AttrField = 'sku' | 'barcode' | 'name' | 'brand' | 'notes' | 'size';

const STOCKS_BULK_LIMIT = 1000;
const ATTRIBUTES_BULK_LIMIT = 500;

const chunk = <T,>(items: T[], size: number): T[][] => {
    const result: T[][] = [];
    for (let i = 0; i < items.length; i += size) result.push(items.slice(i, i + size));
    return result;
};

interface StockCellProps {
    quantity: number | null;
    disabled: boolean;
    onChange: (value: string) => void;
}

const StockCell: React.FC<StockCellProps> = ({ quantity, disabled, onChange }) => {
    const [qty, setQty] = useState<number>(quantity ?? 0);
    const prevRef = useRef<number>(quantity ?? 0);

    useEffect(() => {
        if (quantity !== prevRef.current) {
            prevRef.current = quantity ?? 0;
            setQty(quantity ?? 0);
        }
    }, [quantity]);

    const commit = (val: number) => {
        const clamped = Math.max(0, val);
        setQty(clamped);
        prevRef.current = clamped;
        onChange(String(clamped));
    };

    const btnCls = classNames(
        'w-[18px] h-[18px] flex items-center justify-center rounded-full border text-[13px] leading-none transition-colors flex-shrink-0',
        disabled
            ? 'border-transparent text-slate-300 dark:text-slate-600 cursor-not-allowed'
            : 'border-transparent text-[#d3ddec] dark:text-[#153c76] hover:border-[#2255a1] dark:hover:border-[#2255a1] hover:text-[#2255a1] dark:hover:text-[#d3ddec] cursor-pointer',
    );

    return (
        <div className="flex items-center justify-end gap-[3px] pr-1">
            <button
                className={btnCls}
                disabled={disabled}
                onMouseDown={(e) => { e.preventDefault(); commit(qty - 1); }}
                tabIndex={-1}
            >
                <svg width="8" height="2" viewBox="0 0 8 2" fill="currentColor"><rect width="8" height="2" rx="1"/></svg>
            </button>
            <input
                type="number"
                value={qty}
                size={Math.max(1, String(qty).length)}
                disabled={disabled}
                onChange={(e) => setQty(parseInt(e.target.value) || 0)}
                onBlur={(e) => commit(parseInt(e.target.value) || 0)}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                className={classNames(
                    'h-[26px] px-1 text-center text-[12px] border border-transparent rounded-[3px] bg-white outline-none',
                    'dark:bg-[#1e293b] dark:text-[#d3ddec] focus:border-c-blue dark:focus:border-[#2255a1]',
                    '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                    disabled ? 'opacity-50 cursor-not-allowed' : '',
                )}
                style={{
                    boxSizing: 'border-box',
                    width: 'auto',
                    minWidth: 36,
                    fieldSizing: 'content',
                    MozAppearance: 'textfield',
                } as React.CSSProperties}
            />
            <button
                className={btnCls}
                disabled={disabled}
                onMouseDown={(e) => { e.preventDefault(); commit(qty + 1); }}
                tabIndex={-1}
            >
                <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor"><rect x="3" width="2" height="8" rx="1"/><rect y="3" width="8" height="2" rx="1"/></svg>
            </button>
        </div>
    );
};

interface TextEditCellProps {
    value: string;
    onCommit: (value: string) => void;
    multiline?: boolean;
}

const TextEditCell: React.FC<TextEditCellProps> = ({ value, onCommit, multiline }) => {
    const [val, setVal] = useState(value ?? '');
    useEffect(() => { setVal(value ?? ''); }, [value]);
    const commit = () => onCommit(val);
    const cls = classNames(
        'w-full h-[26px] text-[12px] px-1.5 rounded-[3px] border border-c-blue/60 focus:border-c-blue outline-none bg-white dark:bg-[#0f172a] text-slate-700 dark:text-[#d3ddec]',
    );
    return (
        <input
            type="text"
            value={val}
            className={cls}
            onChange={e => setVal(e.target.value)}
            onBlur={commit}
            onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            onClick={e => e.stopPropagation()}
        />
    );
};

const CopyButton: React.FC<{ value: string }> = ({ value }) => {
    const [copied, setCopied] = React.useState(false);
    const handleCopy = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };
    return (
        <button
            onClick={handleCopy}
            title="Копировать"
            className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 w-[14px] h-[14px] flex items-center justify-center text-slate-400 hover:text-c-blue dark:hover:text-[#d3ddec]"
        >
            {copied ? (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 6l3 3 7-7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            ) : (
                <CopyValueIcon width={14} height={14} fill="currentColor" />
            )}
        </button>
    );
};

const SelectedActionsMenu: React.FC<{ count: number }> = ({ count }) => {
    const [open, setOpen] = React.useState(false);
    const ref = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (!ref.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div ref={ref} className="relative flex items-center">
            <button
                onClick={() => setOpen(v => !v)}
                className="flex items-center gap-[4px] h-[22px] px-[8px] text-[11px] rounded-[3px] border
                    text-[#2255a1] border-[#2255a1] bg-white dark:bg-[#0f172a] dark:text-[#d3ddec] dark:border-[#153c76]
                    hover:bg-[#2255a1] hover:text-white dark:hover:bg-[#153c76] dark:hover:text-[#d3ddec]
                    transition-colors select-none"
            >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M1 3.5h10M1 6.5h10M1 9.5h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
                С выбранными ({count})
                <svg width="8" height="8" viewBox="0 0 8 8" fill="none" className={`transition-transform ${open ? 'rotate-180' : ''}`}>
                    <path d="M1 2.5l3 3 3-3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>

            {open && (
                <div className="absolute bottom-[calc(100%+4px)] left-0 z-30 w-[180px] rounded-[6px] shadow-xl border overflow-hidden
                    bg-[#eef2ff] border-[#d3ddec] dark:bg-[#1e293b] dark:border-[#153c76]">
                    {[
                        { label: 'Добавить в фильтр', action: () => { setOpen(false); } },
                        { label: 'В избранное', action: () => { setOpen(false); } },
                    ].map(({ label, action }) => (
                        <button
                            key={label}
                            onClick={action}
                            className="w-full px-3 py-[7px] text-left text-[12px] transition-colors
                                text-[#2255a1] dark:text-[#eef2ff]
                                hover:bg-[#153c76] hover:text-[#eef2ff]
                                dark:hover:bg-[#eef2ff] dark:hover:text-[#1e293b]"
                        >
                            {label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

interface ITableProps {
    columns: any;
    data: Array<IRowData>;
    rowDataLoading: boolean;
    productsRequestError: string;
    tableSettings: ITableSettingsLegacy;
    setTableSettings: (data: SetStateAction<ITableSettingsLegacy>) => void;
    totalProducts: number;
    tableSettingsLoaded: boolean;
    isNeedUpdateData: boolean;
    setSelectedRows: any;
    setIdFilter: (state: any) => {};
    setSkuFilter: (state: any) => {};
    setBarcodeFilter: (state: any) => {};
    setSizeFilter: (state: any) => {};
    setNameFilter: (state: any) => {};
    setBrandFilter: (state: any) => {};
    setNotesFilter: (state: any) => {};
    setGreaterThanZeroFilter?: (state: any) => {};
    idFilter: number | null;
    skuFilter: string | null;
    barcodeFilter: string | null;
    sizeFilter: string | null;
    nameFilter: string | null;
    brandFilter: string | null;
    notesFilter: string | null;
    greaterThanZeroFilter?: string | null;
    onSelectAllModeChange?: (isSelectAll: boolean) => void;
    editRowDataElement: (row: IRowData) => AnyAction;
    editingMode: boolean;
    setEditingMode: (value: boolean) => void;
    onSelectedActionsToggle: () => void;
    onLayoutReady?: () => void;
}

const Table: React.FC<ITableProps> = React.memo(({
    columns, data, rowDataLoading, productsRequestError,
    tableSettings, setTableSettings, totalProducts, tableSettingsLoaded,
    isNeedUpdateData, setSelectedRows,
    setIdFilter, setSkuFilter, setBarcodeFilter, setSizeFilter,
    setNameFilter, setBrandFilter, setNotesFilter, setGreaterThanZeroFilter,
    idFilter, skuFilter, barcodeFilter, sizeFilter, nameFilter, brandFilter, notesFilter,
    greaterThanZeroFilter, onSelectAllModeChange, editRowDataElement,
    editingMode, setEditingMode, onSelectedActionsToggle, onLayoutReady,
}) => {
    const dispatch = useAppDispatch();
    const currentSeller = useAppSelector((state) => state.sellers.currentSeller);
    const [hoveredRow, setHoveredRow] = useState<string | null>(null);
    const [hoveredCol, setHoveredCol] = useState<string | null>(null);
    const pendingStocksRef = useRef(new Map<string, { productID: number; warehouseID: string; quantity: number }>());
    const pendingAttrsRef = useRef(new Map<number, Partial<Record<AttrField, string>>>());
    const originalRowsRef = useRef(new Map<number, IRowData>());
    const dataRef = useRef(data);
    dataRef.current = data;
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const theadRef = useRef<HTMLTableSectionElement>(null);
    const footerRef = useRef<HTMLDivElement>(null);

    const defaultColumn = React.useMemo(() => ({
        Cell: CustomTableCell,
        minWidth: 75,
        width: 75,
    }), []);

    const HIDDEN_COLS_KEY = 'stocksFBS_hiddenColumns';
    const savedHiddenColumns: string[] = (() => {
        try { return JSON.parse(localStorage.getItem(HIDDEN_COLS_KEY) || '[]'); } catch { return []; }
    })();

    const COL_ORDER_KEY = 'stocksFBS_columnOrder';
    const savedColumnOrder: string[] = (() => {
        try { return JSON.parse(localStorage.getItem(COL_ORDER_KEY) || '[]'); } catch { return []; }
    })();

    const selectedIdsRef = useRef<Set<string>>(new Set());
    const selectAllModeRef = useRef<boolean>(false);
    const justAutoSelectedRef = useRef<boolean>(false);

    const [masterSelectState, setMasterSelectState] = React.useState<0 | 1 | 2>(0);

    const {
        getTableProps, getTableBodyProps, headerGroups, prepareRow,
        page, setPageSize, selectedFlatRows, allColumns, setColumnOrder, toggleAllRowsSelected, toggleRowSelected,
        state: { pageSize, selectedRowIds, columnOrder, sortBy },
    } = useTable<IRowData>(
        { columns, data, defaultColumn, autoResetFilters: false, autoResetSortBy: false, autoResetSelectedRows: false, manualSortBy: true, initialState: { hiddenColumns: savedHiddenColumns, columnOrder: savedColumnOrder } },
        useSticky, useColumnOrder, useSortBy, useResizeColumns, usePagination, useRowSelect,
        (hooks) => {
            hooks.visibleColumns.push((cols) => [
                {
                    id: 'selection',
                    Header: ({ getToggleAllRowsSelectedProps }: any) => (
                        <div className="flex justify-center content-center">
                            <IndeterminateCheckbox name="" {...getToggleAllRowsSelectedProps()} />
                        </div>
                    ),
                    Cell: ({ row }: any) => (
                        <div className="flex justify-center content-center">
                            <IndeterminateCheckbox name="" {...row.getToggleRowSelectedProps()} />
                        </div>
                    ),
                    minWidth: 34, maxWidth: 34, width: 34,
                },
                ...cols,
            ]);
        }
    );

    const hiddenColIds = allColumns.filter(c => !c.isVisible).map(c => c.id).join(',');
    useEffect(() => {
        try { localStorage.setItem(HIDDEN_COLS_KEY, JSON.stringify(hiddenColIds ? hiddenColIds.split(',') : [])); } catch {}
    }, [hiddenColIds]);

    useEffect(() => {
        if (columnOrder.length) {
            try { localStorage.setItem(COL_ORDER_KEY, JSON.stringify(columnOrder)); } catch {}
        }
    }, [columnOrder]);

    useEffect(() => {
        if (selectAllModeRef.current) {
            justAutoSelectedRef.current = true;
        }
        // selectedRowIds у react-table ключуются индексом — при смене страницы выделение
        // "наследуется" на новые строки, поэтому возвращаем его только строкам,
        // реально присутствующим в selectedIdsRef
        page.forEach(row => {
            const shouldSelect = selectAllModeRef.current || selectedIdsRef.current.has(String(row.original.id));
            // row.toggleRowSelected появляется только после prepareRow — используем
            // instance-метод, иначе на свежей странице деселект молча пропускался
            if (!!selectedRowIds?.[row.id] !== shouldSelect) {
                toggleRowSelected(row.id, shouldSelect);
            }
            if (shouldSelect) selectedIdsRef.current.add(String(row.original.id));
        });
    }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (justAutoSelectedRef.current) {
            justAutoSelectedRef.current = false;
            return;
        }

        const currentPageIds = new Set(page.map(r => String(r.original.id)));
        const selectedIds = new Set(selectedFlatRows.map(r => String(r.original.id)));

        currentPageIds.forEach(id => {
            if (selectedIds.has(id)) {
                selectedIdsRef.current.add(id);
            } else {
                selectedIdsRef.current.delete(id);
            }
        });

        if (selectAllModeRef.current) {
            const allSelected = Array.from(currentPageIds).every(id => selectedIds.has(id));
            if (!allSelected) {
                selectAllModeRef.current = false;
                setMasterSelectState(1);
            }
        }

        if (selectedIdsRef.current.size === 0) {
            setEditingMode(false);
            setMasterSelectState(0);
        }

        setSelectedRows(selectedFlatRows);
    }, [selectedFlatRows]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleMasterSelect = () => {
        if (masterSelectState === 0) {
            page.forEach(row => {
                row.toggleRowSelected(true);
                selectedIdsRef.current.add(String(row.original.id));
            });
            setMasterSelectState(1);
            onSelectAllModeChange?.(false);
        } else if (masterSelectState === 1) {
            selectAllModeRef.current = true;
            setMasterSelectState(2);
            onSelectAllModeChange?.(true);
        } else {
            toggleAllRowsSelected(false);
            selectedIdsRef.current.clear();
            selectAllModeRef.current = false;
            setMasterSelectState(0);
            onSelectAllModeChange?.(false);
        }
    };
    useEffect(() => {
        toggleAllRowsSelected(false);
        selectedIdsRef.current.clear();
        selectAllModeRef.current = false;
        setMasterSelectState(0);
        onSelectAllModeChange?.(false);
    }, [idFilter, skuFilter, barcodeFilter, sizeFilter, nameFilter, brandFilter, notesFilter, greaterThanZeroFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (tableSettingsLoaded) setPageSize(tableSettings.limit);
    }, [tableSettingsLoaded]);

    useEffect(() => {
        if (sortBy.length > 0) {
            setTableSettings(prev => ({
                ...prev,
                sortBy: { columnHeader: sortBy[0].id, sortIndex: sortBy[0].desc ? 'desc' : 'asc' },
                page: 1,
            }));
        } else {
            setTableSettings(prev =>
                prev.sortBy?.columnHeader ? { ...prev, sortBy: {}, page: 1 } : prev
            );
        }
    }, [sortBy]); // eslint-disable-line react-hooks/exhaustive-deps

    const onLayoutReadyRef = useRef(onLayoutReady);
    onLayoutReadyRef.current = onLayoutReady;

    useEffect(() => {
        const el = tableContainerRef.current;
        if (!el) return;
        let raf = 0;
        let ready = false;
        const markReady = () => {
            if (ready) return;
            ready = true;
            onLayoutReadyRef.current?.();
        };
        const update = () => {
            raf = 0;
            const headerH = theadRef.current?.clientHeight ?? 68;
            const footerH = footerRef.current?.clientHeight ?? 34;
            const rowH = 40;
            const available = el.clientHeight - headerH - footerH;
            if (available <= 0) return;
            const rows = Math.max(5, Math.floor(available / rowH));
            setPageSize(rows);
            setTableSettings(prev => (prev.limit !== rows ? { ...prev, limit: rows, page: 1 } : prev));
            markReady();
            // Автоподсчёт только при первой загрузке: после успешного замера перестаём следить,
            // иначе ресайз контейнера затирает выбранный пользователем limit
            obs.disconnect();
        };
        // Несколько изменений размеров за кадр (контейнер, шапка, футер) схлопываются в один расчёт
        const schedule = () => { if (!raf) raf = requestAnimationFrame(update); };
        const obs = new ResizeObserver(schedule);
        obs.observe(el);
        if (theadRef.current) obs.observe(theadRef.current);
        if (footerRef.current) obs.observe(footerRef.current);
        schedule();
        // Если замер не удался (контейнер скрыт), страница всё равно не должна остаться без данных
        const fallback = window.setTimeout(markReady, 1000);
        return () => { obs.disconnect(); cancelAnimationFrame(raf); window.clearTimeout(fallback); };
    }, []);

    const rememberOriginalRow = (row: IRowData) => {
        const productID = Number(row.id);
        if (!originalRowsRef.current.has(productID)) originalRowsRef.current.set(productID, row);
    };

    const flushPendingEdits = () => {
        const sellerID = currentSeller?.info?.data?.id;
        const stockItems = Array.from(pendingStocksRef.current.values());
        const attrItems = Array.from(pendingAttrsRef.current, ([productID, fields]) => ({ productID, ...fields }));
        const originals = new Map(originalRowsRef.current);
        pendingStocksRef.current.clear();
        pendingAttrsRef.current.clear();
        originalRowsRef.current.clear();
        if (!sellerID) return;

        const restore = (colsByProduct: Map<number, string[]>) => {
            colsByProduct.forEach((colIds, productID) => {
                const original = originals.get(productID);
                const current = dataRef.current.find((r) => Number(r.id) === productID);
                if (!original || !current) return;
                const attributes = { ...current.attributes };
                colIds.forEach((colId) => { attributes[colId] = original.attributes[colId]; });
                dispatch(editRowDataElement({ ...current, attributes }));
            });
        };
        const notify = (title: string, description: string) => {
            dispatch(setTemporaryNotifications(defaultNotificationCreator({ title, description, iconType: 'warning' })));
        };

        chunk(stockItems, STOCKS_BULK_LIMIT).forEach((items) => {
            stocksService.bulkUpdateStocks(sellerID, items).catch((err) => {
                const cols = new Map<number, string[]>();
                items.forEach(({ productID, warehouseID }) => {
                    cols.set(productID, [...(cols.get(productID) ?? []), `wh_${warehouseID}`]);
                });
                restore(cols);
                notify('Не удалось сохранить остатки', err?.response?.data?.error?.message || 'Изменения остатков отменены');
            });
        });

        chunk(attrItems, ATTRIBUTES_BULK_LIMIT).forEach((items) => {
            const fieldsOf = (productID: number) => {
                const item = items.find((i) => i.productID === productID);
                return item ? Object.keys(item).filter((k) => k !== 'productID') : [];
            };
            sellerProductsService.bulkUpdateProductAttributes(sellerID, items)
                .then((res) => {
                    const errors: Array<{ productID: number; message?: string }> = res.data?.data?.errors ?? [];
                    if (errors.length === 0) return;
                    restore(new Map(errors.map((e) => [Number(e.productID), fieldsOf(Number(e.productID))])));
                    notify(`Не удалось сохранить поля товаров: ${errors.length}`, errors[0]?.message || 'Изменения отменены');
                })
                .catch((err) => {
                    restore(new Map(items.map((i) => [i.productID, fieldsOf(i.productID)])));
                    notify('Не удалось сохранить поля товаров', err?.response?.data?.message || 'Изменения отменены');
                });
        });
    };

    const flushPendingEditsRef = useRef(flushPendingEdits);
    flushPendingEditsRef.current = flushPendingEdits;

    const wasEditingRef = useRef(false);
    useEffect(() => {
        if (wasEditingRef.current && !editingMode) flushPendingEditsRef.current();
        wasEditingRef.current = editingMode;
    }, [editingMode]);

    useEffect(() => () => flushPendingEditsRef.current(), []);

    const textFieldChangeHandler = (value: string, colId: string, rowId: string) => {
        const row = data.find((r) => r.id === rowId);
        if (!row) return;

        if (editingMode) rememberOriginalRow(row);
        dispatch(editRowDataElement({
            ...row,
            attributes: { ...row.attributes, [colId]: value },
        }));

        if (editingMode) {
            const productID = Number(row.id);
            pendingAttrsRef.current.set(productID, { ...pendingAttrsRef.current.get(productID), [colId]: value });
            return;
        }

        const sellerID = currentSeller?.info?.data?.id;
        if (sellerID) {
            sellerProductsService
                .updateProductAttributes(sellerID, Number(rowId), {
                    [colId as 'sku' | 'barcode' | 'name' | 'brand' | 'notes' | 'size']: value,
                })
                .catch(() => {
                    dispatch(editRowDataElement(row));
                });
        }
    };

    const columnChangeHandler = (value: string, colId: string, rowNumber: any) => {
        rowNumber = parseInt(rowNumber);
        data.forEach((row, i) => {
            if (i === rowNumber) {
                const originalCell = row.attributes[colId];
                const updatedCell = typeof originalCell === 'object' && originalCell !== null
                    ? { ...originalCell, quantity: parseFloat(value) || 0 }
                    : value;
                const updatedRow = {
                    ...row,
                    attributes: { ...row.attributes, [colId]: updatedCell },
                };
                if (editingMode) rememberOriginalRow(row);
                dispatch(editRowDataElement(updatedRow));

                if (editingMode) {
                    const productID = Number(row.id);
                    const warehouseID = colId.replace('wh_', '');
                    pendingStocksRef.current.set(`${productID}|${warehouseID}`, {
                        productID,
                        warehouseID,
                        quantity: parseInt(value, 10) || 0,
                    });
                    return;
                }

                const payload = {
                    sellerID: currentSeller?.info?.data?.id,
                    productID: row.id,
                    warehouseID: colId.replace('wh_', ''),
                    quantity: value,
                };
                if (value === '') {
                    //@ts-ignore
                    dispatch(unsetStockOfProductAtWarehouse(payload));
                } else {
                    //@ts-ignore
                    dispatch(setStockToProductAtWarehouse(payload));
                }
            }
        });
    };

    const cellHoverClass = (rowId: string, colId: string) =>
        classNames(
            'transition-colors',
            hoveredRow === rowId || hoveredCol === colId ? 'bg-blue-50/60 dark:bg-blue-900/20' : '',
            hoveredRow === rowId ? 'border-l-2 border-l-blue-400 dark:border-l-blue-500' : 'border-l-2 border-l-transparent',
        );

    return (
        <div ref={tableContainerRef} className="table-main overflow-hidden w-full flex flex-col h-full rounded-tl-lg">
            <div className="flex-1 min-h-0 relative overflow-auto overscroll-contain dark:bg-[#0f172a]">
                    {rowDataLoading && (
                        <div className="absolute h-1/2 w-full top-[94px] flex justify-center items-center z-10">
                            <ComponentsLoader size={36} />
                        </div>
                    )}

                    <table className="react-table sticky" cellSpacing="0" cellPadding="0" {...getTableProps()}>
                        <thead ref={theadRef} className="header">
                            {headerGroups.map((headerGroup, index) => (
                                <HeaderRow
                                    key={index}
                                    headerGroup={headerGroup}
                                    isNeedUpdateData={isNeedUpdateData}
                                    setIdFilter={setIdFilter}
                                    setSkuFilter={setSkuFilter}
                                    setBarcodeFilter={setBarcodeFilter}
                                    setSizeFilter={setSizeFilter}
                                    setNameFilter={setNameFilter}
                                    setBrandFilter={setBrandFilter}
                                    setNotesFilter={setNotesFilter}
                                    setGreaterThanZeroFilter={setGreaterThanZeroFilter}
                                    idFilter={idFilter}
                                    skuFilter={skuFilter}
                                    barcodeFilter={barcodeFilter}
                                    sizeFilter={sizeFilter}
                                    nameFilter={nameFilter}
                                    brandFilter={brandFilter}
                                    notesFilter={notesFilter}
                                    greaterThanZeroFilter={greaterThanZeroFilter}
                                    rowDataLoading={rowDataLoading}
                                    allColumns={allColumns as any[]}
                                    selectedCount={selectedFlatRows.length}
                                    onSelectedActionsToggle={onSelectedActionsToggle}
                                    setColumnOrder={setColumnOrder as any}
                                    masterSelectState={masterSelectState}
                                    onMasterSelectClick={handleMasterSelect}
                                />
                            ))}
                        </thead>

                        <tbody {...getTableBodyProps()} className="body">
                            {!rowDataLoading && data.length > 0 && !productsRequestError
                                ? page.map((row) => {
                                    prepareRow(row);
                                    const isRowHovered = hoveredRow === row.id;
                                    return (
                                        <tr
                                            {...row.getRowProps()}
                                            key={row.id}
                                            onMouseEnter={() => setHoveredRow(row.id)}
                                            onMouseLeave={() => setHoveredRow(null)}
                                            className={classNames(
                                                'group',
                                                isRowHovered ? 'bg-blue-50/60 dark:bg-blue-900/20' : '',
                                            )}
                                        >
                                            {row.cells.map((cell, ci) => {
                                                const isColHovered = hoveredCol === cell.column.id;
                                                const isRowSelected = !!selectedRowIds[row.id];
                                                const inEditMode = editingMode && isRowSelected;

                                                if (cell.column.id === 'selection') {
                                                    return (
                                                        <td
                                                            className={classNames(
                                                                'sticky left-0',
                                                                isRowHovered || isColHovered ? 'bg-blue-50/60 dark:bg-blue-900/20' : 'bg-white dark:bg-[#0f172a]',
                                                            )}
                                                            {...cell.getCellProps()}
                                                            data-sticky-td={true}
                                                        >
                                                            {cell.render('Cell')}
                                                        </td>
                                                    );
                                                }

                                                if (cell.column.id === 'photo') {
                                                    return (
                                                        <td
                                                            {...cell.getCellProps()}
                                                            className={classNames(
                                                                'sticky p-[3px] z-10',
                                                                isRowHovered || isColHovered ? 'bg-blue-50/60 dark:bg-blue-900/20' : 'bg-white dark:bg-[#0f172a]',
                                                            )}
                                                            data-sticky-td={true}
                                                        >
                                                            {cell.value ? (
                                                                <img
                                                                    src={cell.value}
                                                                    alt=""
                                                                    className="w-[36px] h-[36px] rounded object-cover block"
                                                                />
                                                            ) : (
                                                                <div className="w-[36px] h-[36px] flex items-center justify-center rounded bg-slate-100 dark:bg-slate-700">
                                                                    <ImagePlaceholder width={20} height={20} className="text-slate-300 dark:text-slate-500" style={{ fill: 'currentColor' }} />
                                                                </div>
                                                            )}
                                                        </td>
                                                    );
                                                }

                                                if (cell.column.id === 'sku') {
                                                    return (
                                                        <td
                                                            {...cell.getCellProps()}
                                                            onMouseEnter={() => setHoveredCol(cell.column.id)}
                                                            onMouseLeave={() => setHoveredCol(null)}
                                                            className={classNames(
                                                                'px-1',
                                                                isRowHovered || isColHovered ? 'bg-blue-50/60 dark:bg-blue-900/20' : '',
                                                                isRowHovered ? 'border-l-2 border-l-blue-400 dark:border-l-blue-600' : 'border-l-2 border-l-transparent',
                                                            )}
                                                        >
                                                            {inEditMode ? (
                                                                <TextEditCell value={cell.value ?? ''} onCommit={v => textFieldChangeHandler(v, 'sku', row.original.id)} />
                                                            ) : (
                                                                <div className="group flex items-center gap-1 min-w-0">
                                                                    <div className="truncateText min-w-0 flex-1" title={cell.value ?? ''}>
                                                                        <p>
                                                                            <Link
                                                                                target="_blank"
                                                                                to={url(`/:sellerId/pim/edit/${row.original.id}`, currentSeller.info.data?.id)}
                                                                                className="text-c-blue hover:underline"
                                                                            >
                                                                                {cell.value}
                                                                            </Link>
                                                                        </p>
                                                                    </div>
                                                                    <CopyButton value={String(cell.value ?? '')} />
                                                                </div>
                                                            )}
                                                        </td>
                                                    );
                                                }

                                                if (cell.column.id === 'barcode') {
                                                    const value = cell.value?.toString() ?? '';
                                                    const len = value.length;
                                                    const first = len > 6 ? value.slice(0, len - 6) : '';
                                                    const last = len > 6 ? value.slice(len - 6) : value;
                                                    return (
                                                        <td
                                                            {...cell.getCellProps()}
                                                            onMouseEnter={() => setHoveredCol(cell.column.id)}
                                                            onMouseLeave={() => setHoveredCol(null)}
                                                            className={classNames(
                                                                'px-1',
                                                                inEditMode ? '' : 'text-right',
                                                                isRowHovered || isColHovered ? 'bg-blue-50/60 dark:bg-blue-900/20' : '',
                                                            )}
                                                        >
                                                            {inEditMode ? (
                                                                <TextEditCell value={value} onCommit={v => textFieldChangeHandler(v, 'barcode', row.original.id)} />
                                                            ) : (
                                                                <div className="group flex items-center gap-1 justify-end">
                                                                    <span className="text-[12px]">{first && <span className="text-[11px] opacity-70">{first}</span>}<b>{last}</b></span>
                                                                    <CopyButton value={value} />
                                                                </div>
                                                            )}
                                                        </td>
                                                    );
                                                }

                                                if (['name', 'brand', 'notes', 'size'].includes(cell.column.id)) {
                                                    return (
                                                        <td
                                                            {...cell.getCellProps()}
                                                            onMouseEnter={() => setHoveredCol(cell.column.id)}
                                                            onMouseLeave={() => setHoveredCol(null)}
                                                            className={classNames(
                                                                'px-1',
                                                                isRowHovered || isColHovered ? 'bg-blue-50/60 dark:bg-blue-900/20' : '',
                                                            )}
                                                        >
                                                            {inEditMode ? (
                                                                <TextEditCell value={typeof cell.value === 'string' ? cell.value : ''} onCommit={v => textFieldChangeHandler(v, cell.column.id, row.original.id)} />
                                                            ) : (
                                                                <div className="truncateText" title={typeof cell.value === 'string' ? cell.value : ''}>{cell.render('Cell')}</div>
                                                            )}
                                                        </td>
                                                    );
                                                }

                                                if (cell.column.id === 'id') {
                                                    return (
                                                        <td
                                                            {...cell.getCellProps()}
                                                            onMouseEnter={() => setHoveredCol(cell.column.id)}
                                                            onMouseLeave={() => setHoveredCol(null)}
                                                            className={classNames(
                                                                isRowHovered || isColHovered ? 'bg-blue-50/60 dark:bg-blue-900/20' : '',
                                                            )}
                                                        >
                                                            <div className="group flex items-center gap-1 min-w-0">
                                                                <span className="text-[12px] truncate">{cell.value}</span>
                                                                <CopyButton value={String(cell.value ?? '')} />
                                                            </div>
                                                        </td>
                                                    );
                                                }

                                                const cellObj = !React.isValidElement(cell.value) && typeof cell.value === 'object' && cell.value !== null ? cell.value : null;
                                                const q = cellObj !== null ? (typeof cellObj.quantity !== 'undefined' ? cellObj.quantity : null) : cell.value;
                                                const isEditableColumn = cellObj !== null && (!cellObj.isAutoUpdating || inEditMode);

                                                const isCenterCol = cell.column.id.startsWith('wh_') || cell.column.id === 'reserved' || cell.column.id === 'inStock';
                                                return (
                                                    <td
                                                        {...cell.getCellProps()}
                                                        id={cell.column.id}
                                                        key={cell.column.id}
                                                        onMouseEnter={() => setHoveredCol(cell.column.id)}
                                                        onMouseLeave={() => setHoveredCol(null)}
                                                        className={classNames(
                                                            isRowHovered || isColHovered ? 'bg-blue-50/60 dark:bg-blue-900/20' : '',
                                                        )}
                                                        style={isCenterCol ? { textAlign: 'center' } : {}}
                                                    >
                                                        {isEditableColumn ? (
                                                            <StockCell
                                                                quantity={cellObj?.quantity ?? 0}
                                                                disabled={isNeedUpdateData}
                                                                onChange={(value) => columnChangeHandler(value, cell.column.id, row.id)}
                                                            />
                                                        ) : isNeedUpdateData ? (
                                                            <Skeleton containerClassName="w-full" />
                                                        ) : (
                                                            <div className={isCenterCol ? 'flex justify-center pr-1' : 'text-right flex justify-end pr-1'}>
                                                                {q !== null && q !== '' ? q : '-'}
                                                            </div>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })
                                : (
                                    <tr>
                                        <td colSpan={headerGroups[0]?.headers?.length ?? 1} />
                                    </tr>
                                )
                            }
                        </tbody>
                    </table>

                    {!rowDataLoading && data.length <= 0 && !productsRequestError && (
                        <div className="bg-white dark:bg-[#0f172a] border-b border-l border-r border-[#eef2ff] dark:border-slate-700 p-2 text-center dark:text-[#d3ddec]">
                            У вас еще нет продуктов.{' '}
                            <a href={`/a/${currentSeller?.info?.data?.id}/pim/import`} className="text-c-blue dark:text-[#d3ddec] hover:underline">
                                Добавить
                            </a>
                        </div>
                    )}
            </div>

            {productsRequestError && (
                <div className="font-semibold text-center my-4 text-red-500">
                    При получении продуктов произошла ошибка: {productsRequestError}
                </div>
            )}

            {/* Pagination footer */}
            <div ref={footerRef} className="shrink-0 bg-white dark:bg-gray-900 border-t border-gray-200">
                <TableFooter
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    setTableSettings={setTableSettings}
                    tableSettings={tableSettings}
                    tableSettingsLoaded={tableSettingsLoaded}
                    totalProducts={totalProducts}
                    selectedCount={selectedFlatRows.length}
                />
            </div>
        </div>
    );
});

export default Table;
